import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo, WordmarkTagline } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { safeRedirect } from "@/lib/redirect";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";

const DEMO_ACCOUNTS = [
  { label: "Admin", email: "admin@vendora.ca", password: "Admin123!" },
  { label: "Supplier", email: "supplier@vendora.ca", password: "Supplier123!" },
  { label: "Merchant", email: "merchant@vendora.ca", password: "Merchant123!" },
  { label: "Affiliate", email: "affiliate@vendora.ca", password: "Affiliate123!" },
  { label: "Creator", email: "creator@vendora.ca", password: "Creator123!" },
  { label: "Fulfillment", email: "fulfillment@vendora.ca", password: "Fulfill123!" },
  { label: "Services", email: "services@vendora.ca", password: "Service123!" },
  { label: "Customer", email: "customer@vendora.ca", password: "Customer123!" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(safeRedirect(sp.get("redirect"), "/app"));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = (acc) => { setEmail(acc.email); setPassword(acc.password); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative bg-primary text-primary-foreground p-12 noise-overlay overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative z-10 flex flex-col h-full justify-between">
          <Link to="/" data-testid="login-logo-link" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_vendora-os/artifacts/pwqbypxs_c95ab58d-87a7-4baa-ba81-1f6a714401b5.jpg" alt="V" className="w-10 h-10 object-contain rounded-sm bg-white p-1" />
            <span className="font-display font-black text-2xl text-white">Vendora</span>
          </Link>
          <div>
            <h1 className="font-display font-black text-5xl leading-[0.95] tracking-tighter">The commerce operating system for UK sellers.</h1>
            <div className="mt-8"><WordmarkTagline inverted /></div>
          </div>
          <div className="text-sm text-white/60">Centralised payments · Vetted UK suppliers · Priced in pounds</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo size={36} /></div>
          <div className="label-eyebrow text-secondary mb-2">Welcome back</div>
          <h2 className="font-display text-3xl font-bold text-primary">Sign in to Vendora</h2>
          <p className="mt-2 text-sm text-muted-foreground">Pick a demo account below or use your credentials.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            {err && (
              <div className="flex items-start gap-2 border border-destructive/30 bg-destructive/5 text-destructive p-3 rounded-sm text-sm">
                <AlertCircle size={16} className="mt-0.5" /> {err}
              </div>
            )}
            <div>
              <label className="label-eyebrow text-foreground mb-2 block">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="v-input" data-testid="login-email" />
            </div>
            <div>
              <label className="label-eyebrow text-foreground mb-2 block">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="v-input" data-testid="login-password" />
            </div>
            <button type="submit" disabled={submitting} className="v-btn-primary w-full" data-testid="login-submit">
              {submitting ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-sm text-center text-muted-foreground">
              No account? <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-register-link">Create one</Link>
            </div>
          </form>

          <div className="mt-10 pt-6 border-t border-border">
            <div className="label-eyebrow text-muted-foreground mb-3">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((a) => (
                <button key={a.email} onClick={() => fillDemo(a)} className="text-left v-btn-outline justify-start text-xs py-2" data-testid={`demo-${a.label.toLowerCase()}`}>
                  <span className="font-mono text-primary">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
